import { useState, useEffect, useRef } from 'react';
import { fetchSwedishTrafficEvents } from '../services/nvdbService';
import { TrafficEvent, Coordinates, MapBounds } from '../types';
import { boundsOverlapRatio } from '../utils/boundsUtils';
import type { SupportedCountry } from '../utils/countryDetection';

export const useTrafficEvents = (
  active: boolean,
  zoom: number,
  _currentMapCenter: Coordinates,
  currentViewBounds: MapBounds | null,
  viewportCountry?: SupportedCountry | null,
) => {
  const [events, setEvents] = useState<TrafficEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);
  const lastCountryRef = useRef<SupportedCountry | null | undefined>(undefined);

  useEffect(() => {
    if (!active || zoom < 8 || !currentViewBounds) {
      if (!active) {
        setEvents([]);
        setError(false);
        lastFetchedBoundsRef.current = null;
      }
      return;
    }

    // Traffic events are Sweden-only. Skip fetch if clearly outside SE.
    if (viewportCountry && viewportCountry !== 'SE') {
      setEvents([]);
      return;
    }

    const countryChanged = lastCountryRef.current !== viewportCountry;
    if (!countryChanged && lastFetchedBoundsRef.current && boundsOverlapRatio(lastFetchedBoundsRef.current, currentViewBounds) > 0.7) {
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const result = await fetchSwedishTrafficEvents(currentViewBounds);
        setEvents(result);
        setError(false);
        lastFetchedBoundsRef.current = currentViewBounds;
        lastCountryRef.current = viewportCountry;
      } catch (err) {
        console.error('Failed to load traffic events:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(load, 700);
    return () => clearTimeout(timer);
  }, [currentViewBounds, active, zoom, viewportCountry]);

  return { events, isLoading, error };
};
